import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];
  #observer;
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          this.#loadVideo();
          this.#observer?.disconnect();
        }
      }, { rootMargin: '220px 0px', threshold: 0.01 });
      this.#observer.observe(this);
      return;
    }

    this.#loadVideo();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.refs.videoElement?.pause?.();
  }

  #loadVideo() {
    if (this.#loaded) return;
    this.#loaded = true;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.setAttribute('preload', videoElement.getAttribute('preload') || 'metadata');
    videoElement.load();
    window.ACEPerformanceGuard?.init?.(this);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
